import React from 'react';
import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import { motion } from 'framer-motion';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';

const ErrorPage = ({ error, resetError }) => {
  const handleRetry = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md mx-auto"
      >
        {/* Error Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="mb-8"
        >
          <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ErrorOutlineIcon className="text-red-500 text-4xl" />
          </div>
        </motion.div>

        {/* Error Message */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            Oops! Something went wrong
          </h1>
          <p className="text-slate-600 mb-6 leading-relaxed">
            {error?.message || "We're experiencing some technical difficulties. Please try again in a moment."}
          </p>
        </motion.div>

        {/* Error Code (if available) */}
        {error?.status && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <div className="inline-block bg-slate-200 text-slate-700 px-3 py-1 rounded-full text-sm font-mono">
              Error {error.status}
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetry}
            startIcon={<RefreshIcon />}
            className="flex-1 sm:flex-none"
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            component={Link}
            to="/"
            startIcon={<HomeIcon />}
            className="flex-1 sm:flex-none"
          >
            Go Home
          </Button>
        </motion.div>

        {/* Additional Help */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-sm text-slate-500"
        >
          <p>If the problem persists, please contact our support team.</p>
          <p className="mt-1">
            <Link to="/contact" className="text-blue-600 hover:underline">
              Contact Support
            </Link>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ErrorPage;
